import { useState } from 'react'
import { ImageOff } from 'lucide-react'
import { useProductQuickView } from './productQuickView/quickViewContext'

type Props = {
  src?: string | null
  alt?: string
  /** px square for the inline cell. Default 28 (28–32 band). */
  size?: number
  /** px square for the hover preview. Default 220. */
  previewSize?: number
  /** PR 10 (plan I5) — the product's ClickPay ID. When set, the thumbnail is
   *  a button that opens the app-wide quick view on that product (the hover
   *  preview stays). Its click never reaches the row underneath. */
  uid?: string
  /** The scope the quick view opens on — 'all' (default) or a hub
   *  marketplace ('Amazon US', 'Walmart', …). */
  marketplace?: string
}

/**
 * Phase 3C product thumbnail. Fixed-width cell (always `size`×`size`, even
 * when empty) so table rows never reflow as images stream in. NULL/error →
 * placeholder icon. Hover anywhere on the cell → larger preview (CSS-only).
 */
export default function ProductThumb({ src, alt = '', size = 22, previewSize = 220, uid, marketplace }: Props) {
  const [broken, setBroken] = useState(false)
  const quickView = useProductQuickView()
  const show = !!src && !broken
  const thumb = (
    <span
      className="group relative inline-flex shrink-0 items-center justify-center
                 overflow-visible align-middle"
      style={{ width: size, height: size }}
    >
      {show ? (
        <img
          src={src as string}
          alt={alt}
          loading="lazy"
          decoding="async"
          onError={() => setBroken(true)}
          className="h-full w-full rounded border border-[var(--border,#e5e7eb)] object-cover bg-white"
        />
      ) : (
        <span
          className="flex h-full w-full items-center justify-center rounded border
                     border-[var(--border,#e5e7eb)] bg-[var(--surface-2,#f3f4f6)]
                     text-[var(--text-muted,#9ca3af)]"
          title="No image"
          aria-label="No image"
        >
          <ImageOff size={Math.round(size * 0.5)} strokeWidth={1.75} />
        </span>
      )}
      {show && (
        // CSS-only hover preview. Positioned above-left of the cell, pointer
        // events off so it never traps the row click. Hidden until group-hover.
        <span
          className="pointer-events-none absolute bottom-full left-0 z-50 mb-1 hidden
                     rounded-md border border-[var(--border,#e5e7eb)] bg-white p-1
                     shadow-lg group-hover:block"
          style={{ width: previewSize, height: previewSize }}
        >
          <img src={src as string} alt={alt} className="h-full w-full rounded object-contain" />
        </span>
      )}
    </span>
  )
  if (!uid || !quickView.available) return thumb
  return (
    <button
      type="button"
      aria-label="Quick view"
      title={`Quick view — how ${uid} is doing`}
      onClick={(e) => { e.stopPropagation(); quickView.open(uid, marketplace) }}
      className="inline-flex shrink-0 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
    >
      {thumb}
    </button>
  )
}
